class SiteHeader {
  constructor(element) {
    this.element = element;
    this.triggers = [...element.querySelectorAll('[data-menu-trigger]')];
    this.mobileToggle = element.querySelector('[data-mobile-menu-toggle]');
    this.mobileMenu = element.querySelector('[data-mobile-menu]');
    this.accountMenu = element.querySelector('[data-account-menu]');
    this.accountTrigger = this.accountMenu?.querySelector('summary');
    this.languageMenus = [...element.querySelectorAll('[data-language-menu]')];

    if (this.accountMenu) this.accountMenu.open = false;

    this.triggers.forEach((trigger) => {
      trigger.addEventListener('click', () => this.toggleDesktopMenu(trigger));
    });

    this.mobileToggle?.addEventListener('click', () => this.toggleMobileMenu());
    this.element.querySelector('.header-language--mobile summary')?.addEventListener('click', () => {
      requestAnimationFrame(() => {
        if (!this.mobileMenu || this.mobileMenu.hidden) return;
        this.mobileMenu.scrollTo({
          top: this.mobileMenu.scrollHeight,
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        });
      });
    });
    this.languageMenus.forEach((languageMenu) => {
      languageMenu.querySelectorAll('[data-language-code]').forEach((button) => {
        button.addEventListener('click', () => this.selectLanguage(button.dataset.languageCode, button.dataset.languageUrl, languageMenu));
      });
    });
    this.accountTrigger?.addEventListener('click', (event) => {
      event.preventDefault();
      const willOpen = !this.accountMenu.open;
      this.closeDesktopMenus();
      this.closeMobileMenu();
      this.accountMenu.open = willOpen;
    });
    window.addEventListener('pageshow', () => {
      if (this.accountMenu) this.accountMenu.open = false;
    });
    document.addEventListener('click', (event) => {
      if (!this.element.contains(event.target)) this.closeDesktopMenus();
      if (this.accountMenu && !this.accountMenu.contains(event.target)) this.accountMenu.open = false;
      this.languageMenus.forEach((languageMenu) => {
        if (!languageMenu.contains(event.target)) languageMenu.open = false;
      });
    });
    this.element.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.closeDesktopMenus();
        this.closeMobileMenu();
        if (this.accountMenu) this.accountMenu.open = false;
        this.languageMenus.forEach((languageMenu) => {
          languageMenu.open = false;
        });
      }
    });
  }

  toggleDesktopMenu(activeTrigger) {
    const willOpen = activeTrigger.getAttribute('aria-expanded') !== 'true';
    this.closeDesktopMenus();
    if (!willOpen) return;

    const menu = document.getElementById(activeTrigger.getAttribute('aria-controls'));
    activeTrigger.setAttribute('aria-expanded', 'true');
    menu.hidden = false;
  }

  closeDesktopMenus() {
    this.triggers.forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'false');
      const menu = document.getElementById(trigger.getAttribute('aria-controls'));
      if (menu) menu.hidden = true;
    });
  }

  selectLanguage(languageCode, languageUrl, languageMenu) {
    const languageForm = languageMenu?.querySelector('form');
    const languageInput = languageForm?.querySelector('[data-language-input]');
    if (!languageForm || !languageInput || !languageCode) return;
    if (languageUrl) {
      window.location.assign(languageUrl);
      return;
    }
    languageInput.value = languageCode;
    languageInput.setAttribute('value', languageCode);
    this.languageMenus.forEach((menu) => {
      menu.open = false;
    });
    HTMLFormElement.prototype.submit.call(languageForm);
  }

  toggleMobileMenu() {
    if (!this.mobileToggle || !this.mobileMenu) return;
    const willOpen = this.mobileToggle.getAttribute('aria-expanded') !== 'true';
    this.mobileToggle.setAttribute('aria-expanded', String(willOpen));
    this.mobileToggle.setAttribute(
      'aria-label',
      willOpen ? this.mobileToggle.dataset.closeLabel : this.mobileToggle.dataset.openLabel,
    );
    this.mobileMenu.hidden = !willOpen;
    document.documentElement.classList.toggle('menu-open', willOpen);
  }

  closeMobileMenu() {
    if (!this.mobileToggle || !this.mobileMenu) return;
    this.mobileToggle.setAttribute('aria-expanded', 'false');
    this.mobileMenu.hidden = true;
    document.documentElement.classList.remove('menu-open');
  }
}

class USPBar {
  constructor(element) {
    this.element = element;
    this.track = element.querySelector('[data-usp-track]');
    this.slides = [...element.querySelectorAll('[data-usp-slide]')];
    this.indicators = [...element.querySelectorAll('[data-usp-indicator]')];
    this.previousButton = element.querySelector('[data-usp-previous]');
    this.nextButton = element.querySelector('[data-usp-next]');
    this.desktopMedia = window.matchMedia('(min-width: 48rem)');
    this.currentIndex = 0;
    this.touchStartX = null;

    this.previousButton?.addEventListener('click', () => this.show(this.currentIndex - 1));
    this.nextButton?.addEventListener('click', () => this.show(this.currentIndex + 1));
    this.element.addEventListener('touchstart', (event) => this.onTouchStart(event), { passive: true });
    this.element.addEventListener('touchend', (event) => this.onTouchEnd(event), { passive: true });
    this.desktopMedia.addEventListener('change', () => this.updateAccessibility());
    this.show(0);
  }

  show(index) {
    this.currentIndex = (index + this.slides.length) % this.slides.length;
    this.track.style.transform = `translateX(-${this.currentIndex * 100}%)`;
    this.slides.forEach((slide, slideIndex) => {
      const isActive = slideIndex === this.currentIndex;
      slide.classList.toggle('is-active', isActive);
    });
    this.indicators.forEach((indicator, indicatorIndex) => {
      indicator.classList.toggle('is-active', indicatorIndex === this.currentIndex);
    });
    this.updateAccessibility();
  }

  updateAccessibility() {
    this.slides.forEach((slide, slideIndex) => {
      const isHidden = !this.desktopMedia.matches && slideIndex !== this.currentIndex;
      slide.setAttribute('aria-hidden', String(isHidden));
    });
  }

  onTouchStart(event) {
    this.touchStartX = event.changedTouches[0].clientX;
  }

  onTouchEnd(event) {
    if (this.touchStartX === null) return;
    const distance = event.changedTouches[0].clientX - this.touchStartX;
    this.touchStartX = null;
    if (Math.abs(distance) < 40) return;
    this.show(this.currentIndex + (distance < 0 ? 1 : -1));
  }
}

class CookieBannerCopy {
  constructor() {
    this.copy = {
      title: document.body.dataset.cookieBannerTitle,
      body: document.body.dataset.cookieBannerBody,
      privacy: document.body.dataset.cookieBannerPrivacy,
      accept: document.body.dataset.cookieBannerAccept,
      decline: document.body.dataset.cookieBannerDecline,
      manage: document.body.dataset.cookieBannerManage,
    };
    this.observer = new MutationObserver(() => {
      if (this.apply()) this.observer.disconnect();
    });
    this.observer.observe(document.body, { childList: true, subtree: true });
    if (this.apply()) this.observer.disconnect();
  }

  apply() {
    const banner = document.getElementById('shopify-pc__banner');
    if (!banner) return false;

    const title = banner.querySelector('#shopify-pc__banner__body-title');
    const body = banner.querySelector('.shopify-pc__banner__body p');
    const privacyLink = banner.querySelector('#shopify-pc__banner__body-policy-link');
    const acceptButton = banner.querySelector('#shopify-pc__banner__btn-accept');
    const declineButton = banner.querySelector('#shopify-pc__banner__btn-decline');
    const manageButton = banner.querySelector('#shopify-pc__banner__btn-manage-prefs');

    if (title && this.copy.title) title.textContent = this.copy.title;
    if (body && this.copy.body) body.textContent = this.copy.body;
    if (privacyLink && this.copy.privacy) privacyLink.textContent = this.copy.privacy;
    if (acceptButton && this.copy.accept) acceptButton.textContent = this.copy.accept;
    if (declineButton && this.copy.decline) declineButton.textContent = this.copy.decline;
    if (manageButton && this.copy.manage) manageButton.textContent = this.copy.manage;

    this.attachConsentFallback(banner, acceptButton, true);
    this.attachConsentFallback(banner, declineButton, false);

    return true;
  }

  attachConsentFallback(banner, button, allowTracking) {
    if (!button || button.dataset.consentFallbackAttached === 'true') return;
    button.dataset.consentFallbackAttached = 'true';

    button.addEventListener('click', () => {
      window.setTimeout(() => {
        if (!document.body.contains(banner) || banner.hidden) return;
        this.setTrackingConsent(banner, allowTracking);
      }, 250);
    });
  }

  setTrackingConsent(banner, allowTracking) {
    const hideBanner = () => {
      banner.hidden = true;
      banner.setAttribute('aria-hidden', 'true');
      banner.style.setProperty('display', 'none', 'important');
    };
    const updateConsent = () => {
      if (!window.Shopify?.customerPrivacy?.setTrackingConsent) return;
      window.Shopify.customerPrivacy.setTrackingConsent(
        {
          analytics: allowTracking,
          marketing: allowTracking,
          preferences: allowTracking,
          sale_of_data: allowTracking,
        },
        hideBanner,
      );
    };

    if (window.Shopify?.customerPrivacy?.setTrackingConsent) {
      updateConsent();
      return;
    }
    window.Shopify?.loadFeatures?.(
      [{ name: 'consent-tracking-api', version: '0.1' }],
      (error) => {
        if (!error) updateConsent();
      },
    );
  }
}

class CountrySuggestion {
  constructor(element) {
    this.element = element;
    this.storageKey = `detailshop:country-suggestion:${element.dataset.countryCode}`;

    try {
      if (window.localStorage.getItem(this.storageKey)) return;
    } catch (error) {
      // The suggestion still works when storage is unavailable.
    }

    this.element.hidden = false;
    this.element.querySelector('[data-country-confirm]')?.addEventListener('click', () => this.confirmCountry());
    this.element.querySelector('[data-country-dismiss]')?.addEventListener('click', () => this.dismiss());
    this.element.querySelector('[data-country-other]')?.addEventListener('click', () => this.dismiss());
  }

  confirmCountry() {
    const targetDomain = this.element.dataset.countryDomain;
    if (!targetDomain || window.location.hostname === targetDomain) {
      this.dismiss();
      return;
    }

    window.location.assign(`https://${targetDomain}${window.location.pathname}${window.location.search}${window.location.hash}`);
  }

  dismiss() {
    this.element.hidden = true;
    try {
      window.localStorage.setItem(this.storageKey, 'dismissed');
    } catch (error) {
      // Hiding the suggestion does not depend on storage access.
    }
  }
}

document.querySelectorAll('[data-site-header]').forEach((header) => new SiteHeader(header));
document.querySelectorAll('[data-usp-bar]').forEach((bar) => new USPBar(bar));
document.querySelectorAll('[data-country-suggestion]').forEach((suggestion) => new CountrySuggestion(suggestion));
new CookieBannerCopy();
